import React from 'react';
import './Legal.css';

const Security = () => {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="container">
          <h1>Security Statement</h1>
          <p className="legal-effective">Last Updated: January 1, 2025</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="legal-content">
            <div className="legal-intro">
              <p>
                At Pathway Labs, security is foundational to everything we build. We understand
                that our customers trust us with sensitive data related to families, youth,
                and communities. This Security Statement outlines our commitment to protecting
                that trust through robust security practices.
              </p>
            </div>

            <div className="legal-section">
              <h2>Our Security Commitment</h2>
              <p>
                Pathway Labs is built on a foundation of integrity. We believe that organizations
                doing the hardest work deserve infrastructure they can trust. Our security
                approach is designed to:
              </p>
              <ul>
                <li>Protect the confidentiality, integrity, and availability of your data</li>
                <li>Meet the security expectations of mission-driven organizations</li>
                <li>Continuously improve our security posture as threats evolve</li>
                <li>Maintain transparency about our security practices</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>Infrastructure Security</h2>

              <h3>Cloud Infrastructure</h3>
              <p>
                Our services are hosted on industry-leading cloud infrastructure providers that
                maintain comprehensive security certifications. We leverage their security
                capabilities while implementing additional controls specific to our platform.
              </p>

              <h3>Network Security</h3>
              <ul>
                <li>Firewalls and network segmentation to isolate systems</li>
                <li>Intrusion detection and prevention systems</li>
                <li>DDoS protection and mitigation</li>
                <li>Regular vulnerability scanning and penetration testing</li>
              </ul>

              <h3>Physical Security</h3>
              <p>
                Our cloud infrastructure providers maintain SOC 2 Type II certified data centers
                with 24/7 physical security, biometric access controls, and environmental protections.
              </p>
            </div>

            <div className="legal-section">
              <h2>Data Protection</h2>

              <h3>Encryption</h3>
              <ul>
                <li>
                  <strong>In Transit:</strong> All data transmitted between your devices and our
                  servers is encrypted using TLS 1.2 or higher
                </li>
                <li>
                  <strong>At Rest:</strong> Data stored in our systems is encrypted using
                  AES-256 encryption
                </li>
                <li>
                  <strong>Key Management:</strong> Encryption keys are managed using secure key
                  management practices with regular rotation
                </li>
              </ul>

              <h3>Data Isolation</h3>
              <p>
                Customer data is logically isolated to prevent unauthorized access between
                organizations. Access controls ensure that users can only access data they
                are authorized to view.
              </p>

              <h3>Backups</h3>
              <p>
                We maintain regular automated backups of customer data with secure, encrypted
                storage. Backup procedures are tested regularly to ensure data can be recovered
                when needed.
              </p>
            </div>

            <div className="legal-section">
              <h2>Access Control</h2>

              <h3>Authentication</h3>
              <ul>
                <li>Strong password requirements</li>
                <li>Multi-factor authentication (MFA) support</li>
                <li>Session management and automatic timeout</li>
                <li>Secure credential storage using industry-standard hashing</li>
              </ul>

              <h3>Authorization</h3>
              <ul>
                <li>Role-based access control (RBAC)</li>
                <li>Principle of least privilege</li>
                <li>Granular permission settings</li>
                <li>Audit logging of access and changes</li>
              </ul>

              <h3>Internal Access</h3>
              <p>
                Pathway Labs employees access production systems only when necessary and with
                appropriate authorization. All access is logged and regularly reviewed.
                Employees undergo background checks and security training.
              </p>
            </div>

            <div className="legal-section">
              <h2>Application Security</h2>

              <h3>Secure Development</h3>
              <ul>
                <li>Security-focused code review processes</li>
                <li>Static and dynamic application security testing</li>
                <li>Dependency scanning for known vulnerabilities</li>
                <li>Secure coding guidelines and developer training</li>
              </ul>

              <h3>Vulnerability Management</h3>
              <ul>
                <li>Regular security assessments and penetration testing</li>
                <li>Timely patching of identified vulnerabilities</li>
                <li>Monitoring for security advisories affecting our stack</li>
                <li>Responsible disclosure program for security researchers</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>Monitoring and Incident Response</h2>

              <h3>Continuous Monitoring</h3>
              <ul>
                <li>24/7 system monitoring and alerting</li>
                <li>Security event logging and analysis</li>
                <li>Anomaly detection for suspicious activity</li>
                <li>Performance and availability monitoring</li>
              </ul>

              <h3>Incident Response</h3>
              <p>
                We maintain an incident response plan that includes:
              </p>
              <ul>
                <li>Defined roles and responsibilities</li>
                <li>Escalation procedures</li>
                <li>Communication protocols</li>
                <li>Post-incident review and improvement processes</li>
              </ul>
              <p>
                In the event of a security incident affecting customer data, we will notify
                affected customers promptly in accordance with applicable laws and our
                contractual obligations.
              </p>
            </div>

            <div className="legal-section">
              <h2>Business Continuity</h2>
              <ul>
                <li>Redundant systems and failover capabilities</li>
                <li>Disaster recovery planning and testing</li>
                <li>Geographic distribution of critical systems</li>
                <li>Regular backup testing and validation</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>Compliance Considerations</h2>
              <p>
                While Pathway Labs implements security controls aligned with industry standards
                and best practices, please note:
              </p>
              <ul>
                <li>
                  We are a growing company continuously improving our security program
                </li>
                <li>
                  Specific compliance certifications (SOC 2, etc.) may be in progress or planned
                </li>
                <li>
                  Customers are responsible for evaluating whether our security measures meet
                  their specific regulatory requirements
                </li>
                <li>
                  We are happy to discuss our security practices in detail with prospective
                  and current customers
                </li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>Customer Responsibilities</h2>
              <p>
                Security is a shared responsibility. We encourage customers to:
              </p>
              <ul>
                <li>Use strong, unique passwords and enable MFA when available</li>
                <li>Configure appropriate access controls for their users</li>
                <li>Keep their systems and browsers up to date</li>
                <li>Train their staff on security awareness</li>
                <li>Report any suspected security issues promptly</li>
                <li>Review and comply with our acceptable use policies</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>Security Updates</h2>
              <p>
                We continuously improve our security practices. This Security Statement will
                be updated as our security program evolves. We encourage you to review this
                page periodically for the latest information.
              </p>
            </div>

            <div className="legal-section">
              <h2>Contact Us</h2>
              <p>
                If you have questions about our security practices or want to report a
                security concern, please contact us:
              </p>
              <div className="contact-block">
                <p><strong>Pathway Labs, Inc.</strong></p>
                <p>Security inquiries: security@pathwaylabs.io</p>
                <p>General inquiries: hello@pathwaylabs.io</p>
              </div>
              <p className="security-note">
                If you believe you have discovered a security vulnerability, please report it
                to security@pathwaylabs.io. We appreciate responsible disclosure and will
                work with you to understand and address the issue.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Security;
